import { toBlockLifecycleStatus } from '@/blocks/application/sieve/buildBlocksSieveFilter';
import { isDraftOnlyBlock } from '@/blocks/domain/helpers/isDraftOnlyBlock';

/** Minimal version row from `GET /blocks/{id}/detail`. */
export interface BlockDetailVersionRow {
  blockVersionId: string;
  versionNumber: number;
  publishedAt: string | null;
  status?: string;
}

export interface BlockDetailVersionResolutionInput {
  status: string;
  isArchived?: boolean;
  archivedAt?: string | null;
  currentVersion: number;
  currentVersionId?: number | string | null;
  draftVersionId?: number | string | null;
}

function pickCurrentVersion(detail: BlockDetailVersionResolutionInput, versions: BlockDetailVersionRow[]): BlockDetailVersionRow | null {
  if (versions.length === 0) {
    return null;
  }

  const byId = detail.currentVersionId
    ? versions.find((version) => Number(version.blockVersionId) === Number(detail.currentVersionId))
    : null;
  const byNumber = versions.find((version) => version.versionNumber === detail.currentVersion) ?? null;

  return byId ?? byNumber ?? versions.reduce((best, candidate) => (candidate.versionNumber > best.versionNumber ? candidate : best));
}

/**
 * Picks the block version the editor should load into local edit state.
 * Published blocks use the current published pointer; archived blocks are read-only.
 */
export function resolveEditableBlockVersion(
  detail: BlockDetailVersionResolutionInput,
  versions: BlockDetailVersionRow[]
): BlockDetailVersionRow | null {
  if (detail.isArchived === true || (typeof detail.archivedAt === 'string' && detail.archivedAt.trim() !== '')) {
    return null;
  }

  const blockStatus = toBlockLifecycleStatus(detail.status);
  if (blockStatus === 'Unknown' || blockStatus === 'Archived') {
    return null;
  }

  if (detail.draftVersionId != null && String(detail.draftVersionId).trim() !== '') {
    const draft = versions.find((version) => Number(version.blockVersionId) === Number(detail.draftVersionId));
    if (draft) return draft;
  }

  return pickCurrentVersion(detail, versions);
}

/**
 * Published pointer for preview baseline (same as editable source after lifecycle change).
 * A Draft-only block has no published version: the open Draft v1 is never reported as published.
 */
export function resolvePublishedBlockVersion(
  detail: BlockDetailVersionResolutionInput,
  versions: BlockDetailVersionRow[]
): BlockDetailVersionRow | null {
  if (isDraftOnlyBlock(detail)) {
    return null;
  }
  return pickCurrentVersion(detail, versions);
}
